import React, {useMemo, useRef} from 'react';
import {Button, Icon, Text} from '@/ui-kits';
import {Images, NA, PatientAccountTicketRequestStatus} from '@/constants';
import {accountDeletionStatusSelector, authDataSelector} from '@/services/redux/selectors';
import {useSelector} from 'react-redux';
import {formatName} from '@/utils/common_functions';
import { getErrorMessage } from '@/utils/getMessages';
import { useToast } from '@/hooks';
import { maxImageSize } from '@/constants';

interface ProfileImageProps {
  isEditing?: boolean;
  src: string | File;
  onChange?: (photo: File | null) => void; // pass null to delete it
  onEdit?: () => void;
}

export const ProfileImageSection: React.FC<ProfileImageProps> = ({isEditing = false, src, onChange, onEdit}) => {
  // =======================================
  // selectors
  // =======================================
  const authData = useSelector(authDataSelector);
  const accountDeletionStatus = useSelector(accountDeletionStatusSelector);
  const {showToast} = useToast();
  // =======================================
  // states
  // =======================================
  const name = authData?.username;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onChange) {
      // check for size validation
      const imageSize = file.size;
      if (imageSize > maxImageSize) {
        showToast(getErrorMessage('E-10013'), 'failure');
        return;
      }
      onChange(file)
    };
    e.target.value = '';
  };

  const handleDelete = () => onChange?.(null);

  function handleEditProfile() {
    if (!isEditing) {
      onEdit?.();
    }
  }

  const source = useMemo(() => {
    if (!src) return null;
    if (typeof src === 'string') return src;
    return URL.createObjectURL(src);
  }, [src]);

  function updatePhoto() {
    fileInputRef.current?.click();
  }

  return (
    <div className="bg-white rounded-2xl shadow-[0px_4px_41.6px_-9px_rgba(0,0,0,0.15)] p-6 flex flex-col gap-5 items-center">
      <div className="size-[150px] relative rounded-full">
        <img src={source ?? Images.default_profile} alt="Profile" className="size-full rounded-full object-cover object-center" />

        {isEditing && src && (
          <button
            onClick={handleDelete}
            className="absolute bottom-0 right-0 size-9 bg-[#F8FAFC] rounded-full flex items-center justify-center shadow-md transition-colors"
            aria-label="Delete profile photo">
            <Icon type="trash_red" />
          </button>
        )}
      </div>
      {!isEditing && (
        <Text variant="heading" className="font-FigtreeBold text-ui_blue text-3xl -mt-1">
          {formatName(name ?? NA)}
        </Text>
      )}
      {accountDeletionStatus !== PatientAccountTicketRequestStatus.Approved && (!isEditing ? (
        <Button title="Edit Profile" variant="primary" size="md" onClick={handleEditProfile} className="max-w-[180px] w-full" />
      ) : (
        <Button
          title={'Upload Photo'}
          variant="secondary"
          size="md"
          onClick={updatePhoto}
          textClassName="font-PoppinsSemiBold text-sm"
          className=" font-PoppinsSemiBold rounded-lg border-2 w-full max-w-[160px]"
        />
      ))}

      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />

      {isEditing && <p className="text-ui_input_border_dark text-xs font-FigtreeRegular -mt-2 text-center">(JPEG/PNG, max 5MB)</p>}
    </div>
  );
};
